import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { format } from "date-fns";
import { dateTimeFormat } from "../../../common/helpers/formatDateTime";
import { HistoryType, ParsedApprovalChange, ParsedEntryChange } from "../types";
import { useTranslation } from "react-i18next";

interface Props {
  type: HistoryType;
  changes: ParsedEntryChange[] | ParsedApprovalChange[];
  getUserName: (change: any) => string;
}

export const EntryChangeHistoryPopper = ({
  type,
  changes,
  getUserName,
}: Props) => {
  const { t } = useTranslation();

  const isFieldChanges = type === HistoryType.FieldChanges;

  const fieldChangesHeaders = [
    {
      key: "modification",
      label: t("hourEntries:modification"),
      width: "100px",
    },
    { key: "previousValue", label: t("hourEntries:previousValue") },
    { key: "newValue", label: t("hourEntries:newValue") },
    { key: "modifiedBy", label: t("hourEntries:modifiedBy") },
    { key: "timestamp", label: t("hourEntries:timestamp") },
  ];

  const approvalChangesHeaders = [
    { key: "approvalState", label: t("hourEntries:status"), width: "120px" },
    { key: "comment", label: t("hourEntries:comment") },
    { key: "modifiedBy", label: t("hourEntries:modifiedByShort") },
    { key: "timestamp", label: t("hourEntries:timestamp") },
  ];

  const headers = isFieldChanges ? fieldChangesHeaders : approvalChangesHeaders;

  const renderFieldChangesBody = (
    changes: ParsedEntryChange[],
    getUserName: (change: ParsedEntryChange) => string,
  ) => {
    return changes.flatMap((change) =>
      change.fields.map((field, fieldIndex) => (
        <TableRow key={`${change.timestamp}-${fieldIndex}`}>
          <TableCell sx={{ fontSize: "11px" }}>{field.displayName}</TableCell>
          <TableCell sx={{ fontSize: "11px" }}>{field.previousValue}</TableCell>
          <TableCell sx={{ fontSize: "11px" }}>{field.newValue}</TableCell>
          <TableCell sx={{ fontSize: "11px" }}>{getUserName(change)}</TableCell>
          <TableCell sx={{ fontSize: "11px" }}>
            {format(new Date(change.timestamp), dateTimeFormat)}
          </TableCell>
        </TableRow>
      )),
    );
  };

  const renderApprovalChangesBody = (
    changes: ParsedApprovalChange[],
    getUserName: (change: ParsedApprovalChange) => string,
  ) => {
    return changes.map((change) => (
      <TableRow key={change.timestamp}>
        <TableCell sx={{ fontSize: "11px" }}>
          {change.approvalState === "Approved"
            ? t("hourEntries:Approved")
            : t("hourEntries:CustomerRejected")}
        </TableCell>
        <TableCell sx={{ fontSize: "11px" }}>{change.comment || "-"}</TableCell>
        <TableCell sx={{ fontSize: "11px" }}>{getUserName(change)}</TableCell>
        <TableCell sx={{ fontSize: "11px" }}>
          {format(new Date(change.timestamp), dateTimeFormat)}
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <TableContainer
      sx={{
        maxWidth: "800px",
        maxHeight: "600px",
        overflowY: "auto",
        padding: "16px",
      }}
    >
      <Table size="small">
        <TableHead>
          <TableRow sx={{ bgcolor: "background.default" }}>
            {headers.map((header) => (
              <TableCell
                key={header.key}
                width={header.width}
                sx={{ fontWeight: 600, fontSize: "11px" }}
              >
                {header.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isFieldChanges
            ? renderFieldChangesBody(
                changes as ParsedEntryChange[],
                getUserName,
              )
            : renderApprovalChangesBody(
                changes as ParsedApprovalChange[],
                getUserName,
              )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
